import { Check, Copy } from "lucide-react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/useToast";
import type { EventApiResponse } from "@/lib/humhub-api/types";
import { copyEventLinkToClipboard } from "@/lib/utils/eventUtils";

interface StepSuccessProps {
  createdEvent: EventApiResponse;
  onClose: () => void;
}

export const StepSuccess: React.FC<StepSuccessProps> = ({
  createdEvent,
  onClose,
}) => {
  const { t } = useTranslation();
  const isRecurring = Boolean(createdEvent.recurrence_child_ids?.length);

  const handleCopyLink = async () => {
    if (isRecurring) return;

    try {
      await copyEventLinkToClipboard(createdEvent.code, t);
      toast({
        description: t("common.linkCopied"),
      });
    } catch (error) {
      toast({
        variant: "error",
        description:
          error instanceof Error ? error.message : t("common.linkCopyFailed"),
      });
    }
  };

  return (
    <div className="flex h-full flex-col items-center justify-center p-6 text-center">
      <div className="mb-6 flex size-20 items-center justify-center rounded-full bg-rw-main">
        <Check className="size-10 text-white" />
      </div>

      <h2 className="text-2xl font-bold text-rw-black">
        {t("eventCreate.successTitle")}
      </h2>

      <p className="mt-3 text-sm text-rw-dark-gray sm:text-base">
        {isRecurring
          ? t("eventCreate.successRecurringDescription")
          : t("eventCreate.successDescription")}
      </p>

      <div className="mt-6 w-full max-w-md rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-left">
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
          {t("eventCreate.eventName")}
        </div>
        <div className="mt-1 text-sm font-medium text-rw-black-gray">
          {createdEvent.title}
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        {!isRecurring && (
          <Button onClick={handleCopyLink} variant="rw-sub" size="sm" shaped>
            <Copy className="mr-2 size-4" />
            {t("common.copyLink")}
          </Button>
        )}
        <Button onClick={onClose} variant="outline" size="sm" shaped>
          {t("common.close")}
        </Button>
      </div>
    </div>
  );
};
